import JSONModel from "sap/ui/model/json/JSONModel";
import { Routes } from "akademika/akamont/constants/Routes";
import { ServiceMeasurementUnitSelectData } from "akademika/akamont/models/ServiceMeasurementUnitSelectData";
import { ServicePriceResponseDto } from "akademika/akamont/models/ServicePriceResponseDto";
import Select, { Select$ChangeEvent } from "sap/m/Select";
import { TariffPriceTableController } from "../sharedControllers/TariffPriceTableController";
import CustomDatePicker from "akademika/akamont/customControls/CustomDatePicker";
import { GetDateOnlyString } from "akademika/akamont/utils/FormatUtil";
import { MeasurementUnit } from "../../models/MeasurementUnit";
import { ServicePriceCreateDto } from "akademika/akamont/models/ServicePriceCreateDto";
import { ServicePriceExceptionSelectData } from "akademika/akamont/models/ServicePriceExceptionSelectData";
import { IsUserAdmin } from "akademika/akamont/services/AuthService";

/**
 * @namespace akademika.akamont.pages.servicePriceAdd
 */
export default class ServicePriceAdd extends TariffPriceTableController {
  route = Routes.SERVICE_PRICE_ADD;
  parentRoute = Routes.SERVICE_PRICE_LIST;

  private _serviceOptionsPromise: Promise<ServiceMeasurementUnitSelectData[]>;
  private _exceptionSelectDataPromise: Promise<ServicePriceExceptionSelectData>;

  addServicePriceUri = "api/ServicePrice/CreateServicePrice";

  public async onRouteMatched() {
    this.resetViewOptions();
    this.refreshExceptionSelectData();
    await this.refreshServiceMeasurementUnitOptions();
    await this.refreshServicePriceData();

    // Internal users were initially allowed to create and edit price exceptions for their business units.
    // Currently only admin users can edit prices, but the old logic is still in place.
    this.setModel(new JSONModel({ isAdmin: IsUserAdmin() }), "userRoles");
  }

  private refreshExceptionSelectData() {
    this.setModel(new JSONModel(), "exceptionPartnerOptions");
    this._exceptionSelectDataPromise =
      this.fetchModel<ServicePriceExceptionSelectData>(
        "api/ServicePrice/GetExceptionOptions",
        "exceptionSelectData"
      );

    return this._exceptionSelectDataPromise;
  }

  public async onBusinessUnitChange() {
    this.getModel("servicePrice").setProperty("/partnerIds", []);
    await this.updatePartnerOptions();
    await this.refreshDatePickerMinDate();
  }

  private async updatePartnerOptions() {
    const exceptionSelectData = await this._exceptionSelectDataPromise;

    const selectedBpId =
      this.getModel("servicePrice").getProperty("/businessUnitId");

    const matchingBpOption = exceptionSelectData.businessUnits.find(
      (bu) => bu.businessUnitId == Number(selectedBpId)
    );

    let allPartners = new Array;
    exceptionSelectData.businessUnits.map(x => {
      allPartners.push(...x.partners);
    });
    
    const uniqueAllPartners = Array.from(
      new Map(allPartners.map(obj => [obj.partnerId, obj])).values()
    );
    
    const partnerOptions = matchingBpOption ? matchingBpOption.partners : uniqueAllPartners;

    this.setModel(new JSONModel(partnerOptions), "exceptionPartnerOptions");
  }

  public onPartnersChange() {
    this.refreshDatePickerMinDate();
  }

  public async onServiceSelected(oEvent: Select$ChangeEvent) {
    const selectedServiceId = oEvent.getParameter("selectedItem")?.getKey();

    await this.updateMeasurementUnitOptions(Number(selectedServiceId));
  }

  public async onMeasurementUnitSelected() {
    this.checkIfSelectedMeasurementUnitIsMonetary();

    await this.refreshDatePickerMinDate();
  }

  private async checkIfSelectedMeasurementUnitIsMonetary() {
    const measurementUnitSelect = this.byId(
      "serviceMeasurementUnitSelect"
    ) as Select;

    const selectedItem = measurementUnitSelect.getSelectedItem();
    if (!selectedItem) {
      this.setCanTariffsBeEdited(true);
      return;
    }

    const measurementUnit = selectedItem
      .getBindingContext("measurementUnitOptions")
      ?.getProperty("") as MeasurementUnit;

    if (measurementUnit.monetary) {
      this.setCanTariffsBeEdited(false);

      const defaultMonetaryPriceData = await this.get<ServicePriceResponseDto>(
        "api/ServicePrice/GetBlankOrCopyServicePrice",
        {
          params: { monetary: true },
        }
      );

      const servicePriceModel = this.getModel("servicePrice");
      servicePriceModel.setProperty(
        "/isExceptional",
        defaultMonetaryPriceData.isExceptional
      );
      servicePriceModel.setProperty(
        "/contractPriceTariffIntervals",
        defaultMonetaryPriceData.contractPriceTariffIntervals
      );
      servicePriceModel.setProperty(
        "/distanceTariffIntervals",
        defaultMonetaryPriceData.distanceTariffIntervals
      );

      this.refreshTariffIntervalTable();
    } else {
      this.setCanTariffsBeEdited(true);
    }
  }

  // serviceId can be 0 when a blank price is sent from the backend
  private async updateMeasurementUnitOptions(serviceId: number) {
    const serviceOptions = await this._serviceOptionsPromise;
    let serviceOption = serviceOptions.find((so) => so.serviceId === serviceId);

    if (!serviceOption) {
      this.setModel(new JSONModel(), "measurementUnitOptions");
    } else {
      this.setModel(
        new JSONModel(serviceOption.serviceMeasurementUnits),
        "measurementUnitOptions"
      );

      const selectedSmuId = this.getModel("servicePrice").getProperty(
        "/serviceMeasurementUnitId"
      );
      const isPreviousMeasurementUnitSelectionInvalid =
        !selectedSmuId ||
        !serviceOption.serviceMeasurementUnits.some(
          (smu) => smu.serviceMeasurementUnitId === Number(selectedSmuId)
        );

      if (
        isPreviousMeasurementUnitSelectionInvalid &&
        serviceOption.serviceMeasurementUnits.length > 0
      ) {
        // select the first measurement unit option by default
        const measurementUnitSelect = this.byId(
          "serviceMeasurementUnitSelect"
        ) as Select;
        measurementUnitSelect.setSelectedKey(
          String(
            serviceOption.serviceMeasurementUnits[0].serviceMeasurementUnitId
          )
        );
        this.onMeasurementUnitSelected();
      }
    }
  }

  private async refreshServiceMeasurementUnitOptions() {
    this.setModel(new JSONModel(), "measurementUnitOptions");
    this._serviceOptionsPromise = this.fetchModel<
      ServiceMeasurementUnitSelectData[]
    >("api/ServicePrice/GetServiceMeasurementUnitSelectData", "serviceOptions");

    return this._serviceOptionsPromise;
  }

  private async refreshServicePriceData() {
    const servicePriceId = this.routeParams.servicePriceId
      ? this.routeParams.servicePriceId
      : null;

    const servicePriceRespo = await this.fetchModel<ServicePriceResponseDto>(
      "api/ServicePrice/GetBlankOrCopyServicePrice",
      "servicePrice",
      { params: { servicePriceId: servicePriceId } }
    );
    if (servicePriceRespo.partnerId) {
      this.getModel("servicePrice").setProperty("/partnerIds", [
        servicePriceRespo.partnerId,
      ]);
    }

    this.updateMeasurementUnitOptions(servicePriceRespo.serviceId);
    this.setCanTariffsBeEdited(!servicePriceRespo.measurementUnitMonetary);

    this.refreshTariffIntervalTable();
    await this.selectDefaultBusinessUnit();
    await this.updatePartnerOptions();
    await this.refreshDatePickerMinDate();
  }

  public async createServicePrice() {
    const servicePriceDetails =
      this.getModelData<ServicePriceCreateDto>("servicePrice");

    await this.post(this.addServicePriceUri, {
      body: servicePriceDetails,
    });

    this.navTo(Routes.SERVICE_PRICE_LIST);
  }

  private async refreshDatePickerMinDate() {
    const servicePrice =
      this.getModelData<ServicePriceCreateDto>("servicePrice");

    const isTherePriceValidToday = await this.get<boolean>(
      "api/ServicePrice/IsTherePriceValidToday",
      {
        params: {
          serviceMeasurementUnitId: servicePrice.serviceMeasurementUnitId,
          partnerIds: servicePrice.partnerIds,
          businessUnitId: servicePrice.businessUnitId,
        },
      }
    );

    let defaultDate: Date, minDate: Date;
    const datePicker = this.byId("validFrom") as CustomDatePicker;

    if (!isTherePriceValidToday) {
      defaultDate = new Date();
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      minDate = oneYearAgo; // if timeline is empty, allow adding to history
    } else {
      defaultDate = this.getTomorrowDate();
      minDate = this.getTomorrowDate();
    }
    const selectedDate = datePicker.getDateValue();
    if (!selectedDate || selectedDate < minDate) {
      datePicker.setValue(GetDateOnlyString(defaultDate) as string);
    }
    datePicker.setMinDate(minDate);
  }

  private getTomorrowDate() {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    return date;
  }

  private async selectDefaultBusinessUnit() {
    const businessUnitOptions = await this._exceptionSelectDataPromise;
    if (businessUnitOptions.businessUnits.length != 1) return;

    // no need to check whether "isExceptional" is true
    this.getModel("servicePrice").setProperty(
      "/businessUnitId",
      businessUnitOptions.businessUnits[0].businessUnitId
    );
    this.updatePartnerOptions();
  }
}
